import axios, { AxiosInstance, AxiosResponse } from 'axios';
import moment from 'moment-timezone';
import { debugLog, debugError } from '../utils/logger';
import { ULTRA_SECURE_BACKEND_URL, buildPedidosUrl, buildOfertasUrl } from '../config';
import { CotizacionData, OfertaData } from '../types';

/**
 * Headers seguros para peticiones HTTP ultra seguras
 */
const ULTRA_SECURE_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

/**
 * Cliente HTTP ultra seguro que usa el backend de test-aut-front
 * para autenticación y proxy de APIs externas
 */
export class UltraSecureHttpClient {
  private readonly baseClient: AxiosInstance;
  private hasActiveSession: boolean = false;
  private sessionExpiresAt: number | null = null;
  private readonly SESSION_DURATION = 60 * 60 * 1000; // 1 hora en milisegundos

  constructor() {
    this.baseClient = axios.create({
      baseURL: ULTRA_SECURE_BACKEND_URL,
      timeout: 30000,
      withCredentials: true, // CRÍTICO! Para enviar cookies automáticamente
      maxRedirects: 0,
      // Rechazar 401 para que el interceptor lo maneje, pero aceptar otros códigos < 500
      validateStatus: (status) => {
        // Rechazar 401 para que el interceptor pueda manejarlo y reintentar
        if (status === 401) return false;
        // Aceptar otros códigos < 500
        return status < 500;
      },
      headers: ULTRA_SECURE_HEADERS,
    });

    this.setupInterceptors();
  }

  /**
   * Configura interceptores para manejar sesiones automáticamente
   */
  private setupInterceptors(): void {
    this.baseClient.interceptors.request.use(async (config) => {
      // NO verificar sesión para el endpoint de inicio de sesión (evitar bucle infinito)
      if (config.url?.includes('/start-session')) {
        debugLog('🍪 Iniciando sesión ultra segura...');
      } else {
        // Verificar si la sesión está activa antes de cada request
        await this.ensureActiveSession();
        debugLog('🍪 Request ultra seguro con cookies automáticas');
      }
      return config;
    });

    this.baseClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Manejar errores 401 - sesión expirada
        if (error.response?.status === 401) {
          debugLog('🔒 Sesión expirada (401) - intentando renovar...');
          debugLog('🔒 URL del request original:', error.config?.url);
          
          // Limpiar sesión actual
          this.clearSession();
          
          // Intentar crear nueva sesión automáticamente
          try {
            await this.startSession();
            debugLog('✅ Sesión renovada automáticamente, reintentando request...');
            
            // Verificar que tenemos la configuración del request original
            if (error.config) {
              // Actualizar la configuración para asegurar que use la nueva sesión
              const retryConfig = {
                ...error.config,
                headers: {
                  ...error.config.headers,
                  ...ULTRA_SECURE_HEADERS,
                },
              };
              
              // Reintentar el request original con la nueva sesión
              return this.baseClient.request(retryConfig);
            } else {
              debugError('❌ No se puede reintentar: configuración del request no disponible');
              throw new Error('Error 401: No se pudo reintentar el request después de renovar la sesión');
            }
          } catch (renewError) {
            debugError('❌ Error renovando sesión:', renewError);
            // Si no podemos renovar la sesión, lanzar un error descriptivo
            throw new Error('Error 401: No se pudo renovar la sesión. Por favor, recarga la página.');
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Asegura que hay una sesión activa
   */
  private async ensureActiveSession(): Promise<void> {
    if (!this.hasActiveSession || this.isSessionExpired()) {
      await this.startSession();
    }
  }

  /**
   * Verifica si la sesión ha expirado
   */
  private isSessionExpired(): boolean {
    if (!this.sessionExpiresAt) return true;
    return Date.now() >= this.sessionExpiresAt;
  }

  /**
   * Limpia la sesión
   */
  private clearSession(): void {
    this.hasActiveSession = false;
    this.sessionExpiresAt = null;
  }

  /**
   * Método público para resetear el estado del cliente (útil para testing)
   * @internal Solo para uso en tests
   */
  resetState(): void {
    this.clearSession();
  }

  /**
   * Inicia una sesión ultra segura con el backend
   */
  private async startSession(): Promise<void> {
    try {
      await this.baseClient.post('/start-session');
      this.hasActiveSession = true;
      this.sessionExpiresAt = Date.now() + this.SESSION_DURATION;
      
      const expiryTime = new Date(this.sessionExpiresAt).toLocaleTimeString();
      debugLog(`✅ Sesión ultra segura iniciada - expira a las ${expiryTime}`);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      const responseData = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      debugError('Error starting ultra secure session:', responseData ?? errorMessage);
      this.clearSession();
      throw new Error(responseData ?? 'Error desconocido al iniciar sesión ultra segura');
    }
  }

  /**
   * Método GET usando el cliente ultra seguro
   */
  async get<T = unknown>(url: string): Promise<T> {
    await this.ensureActiveSession();
    const response: AxiosResponse<T> = await this.baseClient.get(url);
    return response.data;
  }

  /**
   * Método POST usando el cliente ultra seguro
   */
  async post<T = unknown>(url: string, data?: unknown): Promise<T> {
    await this.ensureActiveSession();
    const response: AxiosResponse<T> = await this.baseClient.post(url, data);
    return response.data;
  }

  /**
   * Método request genérico usando el cliente ultra seguro
   */
  async request<T = unknown>(config: { method: 'GET' | 'POST' | 'PUT' | 'DELETE'; url: string; data?: unknown }): Promise<T> {
    await this.ensureActiveSession();
    const response: AxiosResponse<T> = await this.baseClient.request({
      method: config.method.toLowerCase() as 'get' | 'post' | 'put' | 'delete',
      url: config.url,
      data: config.data,
    });
    return response.data;
  }
}

// Instancia global del cliente HTTP ultra seguro
const ultraSecureHttpClient = new UltraSecureHttpClient();

// Exportar la instancia para uso externo
export { ultraSecureHttpClient };

/**
 * Helper function para resetear el estado del singleton (útil para testing)
 * @internal Solo para uso en tests
 */
export function resetUltraSecureHttpClientState(): void {
  ultraSecureHttpClient.resetState();
}

/**
 * Valida parámetros requeridos
 */
export function validateParameters(pedido: string | null, proveedor: string | null): void {
  if (!pedido || pedido.trim() === '') {
    throw new Error('Parámetro "pedido" es requerido');
  }
  if (!proveedor || proveedor.trim() === '') {
    throw new Error('Parámetro "proveedor" es requerido');
  }
}

/**
 * Procesa datos de respuesta
 */
export function processResponseData(parsedData: CotizacionData | CotizacionData[] | null): CotizacionData | CotizacionData[] {
  if (parsedData) {
    return parsedData;
  } else {
    throw new Error('El servicio no retornó datos válidos');
  }
}

/**
 * Extrae la fecha en formato YYYY-MM-DD sin aplicar zona horaria.
 * Si el backend envía ISO (ej. 2025-02-20T00:00:00.000Z), usar solo la parte de fecha
 * evita que en servidor UTC se interprete como día anterior en Colombia.
 */
function toCalendarDateOnly(value: string): string {
  const s = String(value).trim();
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  return s;
}

/**
 * Valida que la fecha máxima de presentación de la oferta no haya expirado.
 * Lanza Error con mensaje para el usuario si fechaMaxima (YYYY-MM-DD) es menor que hoy.
 * fechaMaxima se trata como fecha calendario (sin conversión por timezone) para que
 * el mismo día de vencimiento sea válido en cualquier despliegue (localhost, dev, prod).
 * @internal Lógica de negocio del servicio
 */
export function validateFechaMaximaNoExpirada(data: CotizacionData | CotizacionData[]): void {
  const cotizacion = Array.isArray(data) ? data[0] : data;
  const fechaMaxima = cotizacion?.fechaMaxima;
  if (!fechaMaxima) return;
  const fechaMaximaStr = toCalendarDateOnly(fechaMaxima);
  const hoyStr = moment().tz('America/Bogota').format('YYYY-MM-DD');
  if (fechaMaximaStr < hoyStr) {
    throw new Error(`La oferta expiró, debía presentarse máximo hasta ${fechaMaximaStr}`);
  }
}

/**
 * Parsea JSON de forma segura
 */
export function parseJsonObjectOrNull<T>(jsonString: unknown): T | null {
  if (jsonString === null || jsonString === undefined) {
    return null;
  }
  
  if (typeof jsonString === 'object') {
    return jsonString as T;
  }
  
  if (typeof jsonString === 'string') {
    try {
      return JSON.parse(jsonString) as T;
    } catch {
      return null;
    }
  }
  
  return null;
}

/**
 * Obtiene datos de cotización de manera segura (GET)
 * Usa el backend ultra seguro como proxy
 * @param pedido - Código del pedido
 * @param proveedor - Código del proveedor
 * @param httpClient - Cliente HTTP a usar (opcional, por defecto usa el singleton)
 * @internal httpClient solo para uso en tests
 */
export async function getCotizacionData(
  pedido: string | null, 
  proveedor: string | null,
  httpClient?: UltraSecureHttpClient
): Promise<CotizacionData | CotizacionData[]> {
  try {
    debugLog('📊 [DEBUG] Iniciando obtención de datos de cotización (ultra seguro)...');
    debugLog('📊 [DEBUG] Parámetros - Requisición:', pedido, 'Proveedor:', proveedor);
    
    validateParameters(pedido, proveedor);
    
    // Usar el cliente proporcionado o el singleton por defecto
    const client = httpClient ?? ultraSecureHttpClient;
    
    // Construir la URL relativa al backend ultra seguro que hará proxy
    const externalApiUrl = buildPedidosUrl(pedido!, proveedor!);
    const response = await client.get<{ data: CotizacionData | CotizacionData[] }>(externalApiUrl);
    debugLog('📊 [DEBUG] Respuesta recibida:', response);
    
    const parsedData = parseJsonObjectOrNull<CotizacionData | CotizacionData[]>(response.data || response);
    debugLog('📊 [DEBUG] Datos parseados:', parsedData);
    
    const result = processResponseData(parsedData);
    validateFechaMaximaNoExpirada(result);
    debugLog('✅ [DEBUG] Datos de cotización obtenidos exitosamente');
    return result;
  } catch (error) {
    debugError('❌ [DEBUG] Error en getCotizacionData:', error);
    if (error instanceof Error && (error.message.includes('Parámetro') || error.message.includes('La oferta expiró'))) {
      throw error;
    }
    throw new Error(`Error al obtener datos de cotización: ${error instanceof Error ? error.message : 'Error desconocido'}`);
  }
}

/**
 * Envía cotización de manera segura (POST)
 * Usa el backend ultra seguro como proxy
 * @param ofertaData - Datos de la oferta a enviar
 * @param pedido - Código del pedido
 * @param httpClient - Cliente HTTP a usar (opcional, por defecto usa el singleton)
 * @internal httpClient solo para uso en tests
 */
export async function enviarCotizacion(
  ofertaData: OfertaData,
  pedido: string,
  httpClient?: UltraSecureHttpClient
): Promise<{ success: boolean; message?: string; [key: string]: unknown }> {
  try {
    debugLog('📤 [DEBUG] Iniciando envío de cotización (ultra seguro)...');
    debugLog('📤 [DEBUG] Datos de oferta:', ofertaData);
    debugLog('📤 [DEBUG] Requisición:', pedido);
    
    if (!pedido || pedido.trim() === '') {
      throw new Error('Parámetro "pedido" es requerido');
    }
    
    // Usar el cliente proporcionado o el singleton por defecto
    const client = httpClient ?? ultraSecureHttpClient;
    
    // Construir la URL relativa al backend ultra seguro que hará proxy
    const externalApiUrl = buildOfertasUrl(pedido);

    const response = await client.post<{ success: boolean; message?: string; [key: string]: unknown }>(externalApiUrl, ofertaData);
    debugLog('📤 [DEBUG] Respuesta recibida:', response);
    
    // Si llegamos aquí, el status code es 200 (éxito)
    const parsedData = parseJsonObjectOrNull<{ success: boolean; message?: string; [key: string]: unknown }>(response);
    if (parsedData) {
      return parsedData;
    } else {
      return { success: true, message: 'Cotización enviada exitosamente' };
    }
  } catch (error) {
    debugError('❌ [DEBUG] Error en enviarCotizacion:', error);
    
    // Si el error contiene información del servidor, la devolvemos
    if (error instanceof Error && error.message.includes('Error del servidor')) {
      throw error;
    }
    
    throw new Error(`Error al enviar cotización: ${error instanceof Error ? error.message : 'Error desconocido'}`);
  }
}